/**
 * useEmbryoReport
 *
 * Fetches the embryo genetic report (PGT-A / PGT-M / WGS results) for a cycle.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { throwApiError } from "@src/packages/api/api-error";
import { useApiClient } from "@src/packages/api/louise-api";
import type { RequestBody } from "@src/packages/openapi/helpers";
import type { paths } from "@src/packages/openapi/schema";

type EmbryoReportResponse = NonNullable<
  paths["/cycles/{cycleId}/embryo-report"]["get"]["responses"]["200"]["content"]["application/json"]["data"]
>;
type EmbryoReportHistoryResponse = NonNullable<
  paths["/cycles/{cycleId}/embryo-report/history"]["get"]["responses"]["200"]["content"]["application/json"]["data"]
>;

export type EmbryoReportCreateInput = RequestBody<
  paths["/cycles/{cycleId}/embryo-report"]["post"]
>;
export type EmbryoReportUpdateInput = RequestBody<
  paths["/cycles/{cycleId}/embryo-report"]["patch"]
>;

export type EmbryoReport = EmbryoReportResponse;
export type EmbryoReportHistoryEntry = EmbryoReportHistoryResponse[number];
export type EmbryoGenomicResult = EmbryoReport["embryos"][number];
export type EmbryoMonogenicFinding =
  EmbryoGenomicResult["monogenicFindings"][number];
export type EmbryoPolygenicRisk = EmbryoGenomicResult["polygenicRisks"][number];
export type EmbryoChromosomeCN = NonNullable<
  EmbryoGenomicResult["cnProfile"]
>[number];
export type EmbryoMosaicism = NonNullable<
  EmbryoGenomicResult["mosaicism"]
>[number];

export function useEmbryoReport(cycleId: string | undefined) {
  const { client, tenantId } = useApiClient();

  return useQuery({
    queryKey: ["embryo-report", tenantId, cycleId],
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/cycles/{cycleId}/embryo-report",
        {
          params: {
            path: {
              cycleId: cycleId!,
            },
          },
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to fetch embryo report",
          "useEmbryoReport",
          response?.status,
        );
      }
      return data?.data ?? null;
    },
    enabled: !!cycleId && !!tenantId,
  });
}

export function useEmbryoReportHistory(cycleId: string | undefined) {
  const { client, tenantId } = useApiClient();

  return useQuery({
    queryKey: ["embryo-report-history", tenantId, cycleId],
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/cycles/{cycleId}/embryo-report/history",
        {
          params: {
            path: {
              cycleId: cycleId!,
            },
          },
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to fetch embryo report history",
          "useEmbryoReportHistory",
          response?.status,
        );
      }
      return data?.data ?? [];
    },
    enabled: !!cycleId && !!tenantId,
  });
}

export function useCreateEmbryoReport() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cycleId,
      data,
    }: {
      cycleId: string;
      data: EmbryoReportCreateInput;
    }) => {
      const result = await client.POST("/cycles/{cycleId}/embryo-report", {
        params: { path: { cycleId } },
        body: data,
      });
      if (result.error) {
        throwApiError(
          result.error,
          "Failed to save embryo report",
          "useCreateEmbryoReport",
          result.response?.status,
        );
      }
      return result.data?.data ?? null;
    },
    onSuccess: (_data, { cycleId }) => {
      queryClient.invalidateQueries({
        queryKey: ["embryo-report", tenantId, cycleId],
      });
      queryClient.invalidateQueries({
        queryKey: ["embryo-report-history", tenantId, cycleId],
      });
    },
  });
}

export function useUpdateEmbryoReport() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cycleId,
      data,
    }: {
      cycleId: string;
      data: EmbryoReportUpdateInput;
    }) => {
      const result = await client.PATCH("/cycles/{cycleId}/embryo-report", {
        params: { path: { cycleId } },
        body: data,
      });
      if (result.error) {
        throwApiError(
          result.error,
          "Failed to update embryo report",
          "useUpdateEmbryoReport",
          result.response?.status,
        );
      }
      return result.data?.data ?? null;
    },
    onSuccess: (_data, { cycleId }) => {
      queryClient.invalidateQueries({
        queryKey: ["embryo-report", tenantId, cycleId],
      });
      queryClient.invalidateQueries({
        queryKey: ["embryo-report-history", tenantId, cycleId],
      });
    },
  });
}
